import { useEffect, useMemo, useState } from "react";
import { TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "../../utils/portalHelpers";
import {
  PageHeader,
  Panel,
  StatusChip,
  EmptyState,
  LoadingPanel,
  ErrorPanel,
} from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import {
  fetchPartnerMessages,
  markPartnerMessagesRead,
  sendPartnerMessage,
} from "../../api/endpoints/messages";
import usePartnerQuery from "../../hooks/usePartnerQuery";

const AlertsPage = () => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const params = useMemo(() => ({ page: 0, size: 50 }), []);

  const { data, isLoading, isError, refetch } = usePartnerQuery(
    partnerQueryKeys.messages(params),
    () => fetchPartnerMessages(params)
  );

  const messages = useMemo(() => data?.content || [], [data]);

  const markRead = useMutation(markPartnerMessagesRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(partnerQueryKeys.messages(params));
      queryClient.invalidateQueries(partnerQueryKeys.messages({ view: "unread-count" }));
      queryClient.invalidateQueries(partnerQueryKeys.dashboard());
    },
  });

  const send = useMutation(sendPartnerMessage, {
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries(partnerQueryKeys.messages(params));
    },
  });

  // Opening the inbox is what marks it read; the badge is driven by the same unread count.
  const unreadIds = useMemo(
    () =>
      messages
        .filter((message) => message.direction === "ADMIN_TO_PARTNER" && !message.readAt)
        .map((message) => message.id),
    [messages]
  );

  useEffect(() => {
    if (unreadIds.length && !markRead.isLoading) {
      markRead.mutate(unreadIds);
    }
    // Only react to the set of unread ids; mutating on every render would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadIds.join(",")]);

  return (
    <div>
      <PageHeader title="Alerts / Messages" />

      {isLoading ? (
        <LoadingPanel label="Loading your messages…" />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : (
        <Panel
          title={`${data?.totalItems ?? messages.length} message${
            messages.length === 1 ? "" : "s"
          }`}
        >
          {messages.length ? (
            <div className="space-y-3 mb-4">
              {messages.map((message) => {
                const fromOffice = message.direction === "ADMIN_TO_PARTNER";
                return (
                  <article
                    key={message.id}
                    className={`rounded-2xl border p-4 ${
                      fromOffice && !message.readAt
                        ? "border-teal-200 bg-teal-50/40"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h3 className="text-sm font-bold text-slate-900 m-0">
                        {fromOffice ? message.senderName || "The office" : "You"}
                      </h3>
                      {message.clientName ? (
                        <StatusChip tone="sky">{message.clientName}</StatusChip>
                      ) : (
                        <StatusChip tone="neutral">General</StatusChip>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 m-0 mt-1 whitespace-pre-wrap">
                      {message.body}
                    </p>
                    <p className="text-[11px] text-slate-400 m-0 mt-2">
                      {formatDateTime(message.createdAt)}
                    </p>
                  </article>
                );
              })}
            </div>
          ) : (
            <EmptyState
              title="No messages yet"
              description="Write below and the office will pick it up."
            />
          )}

          <div className="flex flex-col sm:flex-row gap-2 items-start">
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Message the office"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
            <button
              type="button"
              disabled={!draft.trim() || send.isLoading}
              onClick={() => send.mutate({ body: draft.trim(), clientId: null })}
              className="rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-40"
            >
              {send.isLoading ? "Sending…" : "Send"}
            </button>
          </div>
          {send.isError ? (
            <p className="text-xs text-rose-700 mt-2 mb-0">That message did not send. Try again.</p>
          ) : null}
        </Panel>
      )}
    </div>
  );
};

export default AlertsPage;
